// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Huntress — the three-tap turn, as a state machine over the legal ply list.
//
// A turn is pick a huntress → pick where she runs → pick where the arrow lands, and the ONE thing
// that must never happen is a tap that half-commits something illegal. So no move is ever
// assembled and then checked: the machine FILTERS the legal plies as taps arrive, and the squares
// it offers are, by construction, exactly the squares that lead to a legal ply. An illegal
// combination is unreachable rather than rejected.
//
// EXACTLY ONE STAGE IS LIVE AT A TIME, which is also what lets the two target styles be told apart
// on a pale board where everything meaningful has to be dark: the destinations (a filled disc) and
// the arrow squares (an open ring) are never on screen together, so they cannot be confused even by
// a player who sees no colour at all. tests/contrast.test.ts leans on that, so it is a rule, not a
// coincidence.

import { plyArrow, plyFrom, plyTo } from './rules';

export type Stage = 'idle' | 'picked' | 'moved';

export interface TurnUi {
  /** The square the picked huntress started on. */
  from: number | null;
  /** Where she is provisionally standing. */
  to: number | null;
}

export const freshTurn = (): TurnUi => ({ from: null, to: null });

export const stageOf = (ui: TurnUi): Stage => (ui.from === null ? 'idle' : ui.to === null ? 'picked' : 'moved');

/** The plies still reachable from what has been tapped so far. */
export function candidates(legal: number[], ui: TurnUi): number[] {
  if (ui.from === null) return [];
  return legal.filter((p) => plyFrom(p) === ui.from && (ui.to === null || plyTo(p) === ui.to));
}

/** The squares the CURRENT stage is asking for. Empty in `idle` — the prompt carries that one. */
export function targets(legal: number[], ui: TurnUi): Set<number> {
  const out = new Set<number>();
  const stage = stageOf(ui);
  if (stage === 'idle') return out;
  for (const p of candidates(legal, ui)) out.add(stage === 'picked' ? plyTo(p) : plyArrow(p));
  return out;
}

/** Squares holding a huntress this side may pick up. */
export function pickable(legal: number[]): Set<number> {
  const out = new Set<number>();
  for (const p of legal) out.add(plyFrom(p));
  return out;
}

export type TapResult =
  | { kind: 'none' }
  | { kind: 'advance' }
  | { kind: 'cancel' }
  | { kind: 'commit'; ply: number }
  | { kind: 'reject' };

/**
 * Apply a tap. The caller owns the repaint; this owns the truth.
 *
 * Tapping a huntress always re-picks — including tapping a DIFFERENT one mid-move, which silently
 * abandons the half-made move rather than making the player find Undo first. Tapping the picked
 * huntress again puts her down.
 */
export function tap(legal: number[], ui: TurnUi, cell: number): TapResult {
  const stage = stageOf(ui);
  const heads = pickable(legal);

  if (stage !== 'moved' && heads.has(cell)) {
    if (ui.from === cell && ui.to === null) {
      ui.from = null;
      ui.to = null;
      return { kind: 'cancel' };
    }
    ui.from = cell;
    ui.to = null;
    return { kind: 'advance' };
  }

  if (stage === 'idle') return { kind: 'reject' };

  const want = targets(legal, ui);
  if (!want.has(cell)) {
    // A tap on the square she is standing on, mid-move, means "put her back".
    if (stage === 'moved' && cell === ui.to) {
      ui.to = null;
      return { kind: 'cancel' };
    }
    if (stage === 'moved' && cell === ui.from) {
      ui.to = null;
      return { kind: 'cancel' };
    }
    return { kind: 'reject' };
  }

  if (stage === 'picked') {
    ui.to = cell;
    return { kind: 'advance' };
  }

  const ply = candidates(legal, ui).find((p) => plyArrow(p) === cell);
  if (ply === undefined) return { kind: 'reject' };
  return { kind: 'commit', ply };
}

/** One step back: arrow → destination → nothing. Returns false when there was nothing to undo. */
export function undo(ui: TurnUi): boolean {
  if (ui.to !== null) {
    ui.to = null;
    return true;
  }
  if (ui.from !== null) {
    ui.from = null;
    return true;
  }
  return false;
}

export function promptFor(stage: Stage, yours: boolean): string {
  if (!yours) return '';
  if (stage === 'idle') return 'Tap one of your huntresses.';
  if (stage === 'picked') return 'Run her to a marked square.';
  return 'Now loose an arrow — the square it lands on burns.';
}
